import React, { Fragment, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { EventToolbox } from 'client/utils/event-toolbox';
import { useScrollArrows } from 'site-modules/shared/hooks/use-scroll-arrows';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { IMAGE_SIZES } from 'site-modules/shared/constants/editorial/editorial';
import { CAROUSEL_EVENTS } from 'site-modules/shared/components/carousel/events';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { Carousel } from 'site-modules/shared/components/carousel/carousel';
import { CarouselLazySlide } from 'site-modules/shared/components/carousel/carousel-lazy-slide';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';

import './article-photo.scss';

function Slide({ photo, showLineUnderTitle = true, aspectRatio = '3:2', isCore7220Enabled = false, ...restProps }) {
  return (
    <figure className="mb-0" {...restProps}>
      <AspectRatioContainer className={classnames({ 'rounded-12': !isCore7220Enabled })} aspectRatio={aspectRatio}>
        <img
          alt={photo.title || ''}
          className="w-100"
          src={getFullImageUrl(photo.href, IMAGE_SIZES.lg)}
          loading="lazy"
          decoding="async"
        />
      </AspectRatioContainer>
      {!!photo.captionTranscript && (
        <figcaption className={classnames('xsmall py-0_5', { 'title-line': showLineUnderTitle })}>
          {photo.captionTranscript}
        </figcaption>
      )}
    </figure>
  );
}

Slide.propTypes = {
  photo: PropTypes.shape({
    title: PropTypes.string,
    href: PropTypes.string,
    captionTranscript: PropTypes.string,
  }).isRequired,
  showLineUnderTitle: PropTypes.bool,
  aspectRatio: PropTypes.string,
  isCore7220Enabled: PropTypes.bool,
};

const ARROW_CLASS_NAME = 'arrow pos-a top-0 h-100 bg-transparent border-0 text-white rounded-8';

export function ArticlePhoto({
  photos = [],
  className = '',
  showLineUnderTitle = true,
  aspectRatio,
  isLazySlides = false,
  isCore7220Test = false,
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef();
  const [arrows, scrollLeft, scrollRight] = useScrollArrows(carouselRef);

  const beforeChange = useCallback((from, to) => {
    setCurrentSlide(to);
  }, []);

  const onCarouselScroll = useDebounce(() => {
    EventToolbox.fireCustomEvent(CAROUSEL_EVENTS.SWIPE, { skipSwipeTracking: true });
  }, 50);

  if (!photos.length) {
    return null;
  }

  const firstPhotoSlide = <Slide photo={photos[0]} showLineUnderTitle={showLineUnderTitle} aspectRatio={aspectRatio} />;
  const core7220Ctrl = (
    <ClientSideRender placeholder={firstPhotoSlide}>
      {/* Do not set infinite to true since it's affecting SEO with cloned slides */}
      <Carousel infinite={false} beforeChange={beforeChange}>
        {photos.map((photo, ind) => {
          const slideContent = (
            <Slide photo={photo} showLineUnderTitle={showLineUnderTitle} aspectRatio={aspectRatio} />
          );

          return (
            <div key={photo.href}>
              {isLazySlides ? (
                <CarouselLazySlide currentSlide={currentSlide} slideIndex={ind}>
                  {slideContent}
                </CarouselLazySlide>
              ) : (
                slideContent
              )}
            </div>
          );
        })}
      </Carousel>
    </ClientSideRender>
  );

  return (
    <div className={classnames('article-photo', className)}>
      {
        // Render carousel in a case we have several photos
        // or Render single slide image in a case of only one photo
        photos.length > 1 ? (
          <Fragment>
            {isCore7220Test ? (
              <Experiment name="core-7220-photo-carousel-v2" showDefault>
                <Recipe name="ctrl" isDefault>
                  {core7220Ctrl}
                </Recipe>
                <Recipe name="chal">
                  <div className="carousel-wrapper pos-r">
                    <button
                      className={classnames(ARROW_CLASS_NAME, 'left left-0', {
                        invisible: !arrows.left,
                      })}
                      onClick={scrollLeft}
                      aria-label="Show previous photo"
                    >
                      <i className="icon-arrow-left4" aria-hidden />
                    </button>
                    <ul
                      ref={carouselRef}
                      className="photos-carousel d-flex list-unstyled rounded-12 mb-0"
                      onScroll={onCarouselScroll}
                      style={{ flexWrap: 'nowrap', overflowX: 'auto' }}
                    >
                      {photos.map(photo => (
                        <li key={photo.href} className="w-100" style={{ flexShrink: 0 }}>
                          <Slide
                            photo={photo}
                            showLineUnderTitle={showLineUnderTitle}
                            aspectRatio={aspectRatio}
                            isCore7220Enabled
                          />
                        </li>
                      ))}
                    </ul>

                    <button
                      className={classnames(ARROW_CLASS_NAME, 'right right-0', {
                        invisible: !arrows.right,
                      })}
                      onClick={scrollRight}
                      aria-label="Show next photo"
                    >
                      <i className="icon-arrow-right4" aria-hidden />
                    </button>
                  </div>
                </Recipe>
              </Experiment>
            ) : (
              core7220Ctrl
            )}
          </Fragment>
        ) : (
          firstPhotoSlide
        )
      }
    </div>
  );
}

ArticlePhoto.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      href: PropTypes.string,
      captionTranscript: PropTypes.string,
    })
  ),
  className: PropTypes.string,
  showLineUnderTitle: PropTypes.bool,
  aspectRatio: PropTypes.string,
  isLazySlides: PropTypes.bool,
  isCore7220Test: PropTypes.bool,
};
